import { useState } from 'react';
import styled from '@emotion/styled';
import { Tab, Tabs } from '@mui/material';
import type { MessageTestingCell, MessageTestingRollups } from '@trinity/chat/types/message-testing';
import { MessageTestingPivotTable } from './PivotTable';
import { OverallRollupContent, SegmentRollupsContent, CategoryRollupsContent } from './RollupSummary';
import type { TestableMessage, SegmentOption } from './types';

interface MessageTestingResultsTabsProps {
  messages: TestableMessage[];
  segments: SegmentOption[];
  cells: MessageTestingCell[];
  cellErrors: Map<string, string>;
  rollups: MessageTestingRollups | null;
}

const TAB_STYLE = { minHeight: 36, textTransform: 'none' as const, fontSize: 13 };

export function MessageTestingResultsTabs({
  messages,
  segments,
  cells,
  cellErrors,
  rollups,
}: MessageTestingResultsTabsProps): React.JSX.Element {
  const [tab, setTab] = useState(0);
  const summaryDisabled = !rollups;

  const handleChange = (_: React.SyntheticEvent, v: number): void => {
    if (v > 0 && summaryDisabled) return;
    setTab(v);
  };

  return (
    <Root>
      <Tabs value={tab} onChange={handleChange} variant="fullWidth" sx={{ minHeight: 36 }}>
        <Tab label="Matrix" sx={TAB_STYLE} />
        <Tab label="Overall" sx={TAB_STYLE} disabled={summaryDisabled} />
        <Tab label="By Segment" sx={TAB_STYLE} disabled={summaryDisabled} />
        <Tab label="By Category" sx={TAB_STYLE} disabled={summaryDisabled} />
      </Tabs>

      <TabContent>
        {tab === 0 && (
          <MessageTestingPivotTable
            messages={messages}
            segments={segments}
            cells={cells}
            cellErrors={cellErrors}
          />
        )}
        {tab === 1 && rollups && <OverallRollupContent rollup={rollups.matrixRollup} messages={messages} />}
        {tab === 2 && rollups && <SegmentRollupsContent rollups={rollups.segmentRollups} />}
        {tab === 3 && rollups && (
          <CategoryRollupsContent rollups={rollups.categoryRollups} messages={messages} />
        )}
      </TabContent>
    </Root>
  );
}

const Root = styled.div`
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 5px 15px;
`;
